import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { App as CapacitorApp } from '@capacitor/app'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import './i18n'
import { routeTree } from './routeTree.gen'
import { isNative, isAndroid, isIos } from '@/lib/platform'
import { logDocLoad } from '@/lib/powersync/lifecycle-log'
import { releaseForHardLoad } from '@/lib/hard-nav'
import { initAppVersion } from '@/lib/app-version'
import './globals.css'

// First lifecycle entry of every document. Reading back a run of `doc-load`
// entries (with their nav type) tells us whether the WebView is hard-reloading
// — the suspected driver of the native "database is locked (code 5)" collision.
logDocLoad()

// Resolve the app-version tag once (async on native via Capacitor getInfo) so
// apiFetch can stamp X-App-Version onto backend calls — server writes it to
// analyses.app_version for per-build diagnostics. Fire-and-forget; the header is
// simply omitted until it resolves.
void initAppVersion()

// Bridge for the pre-bundle inline chunk-recovery script in index.html: it
// runs in vanilla JS (no module access) and must close PowerSync's native
// SQLite connection before it reloads the document, or its recovery reload
// becomes the next code-5 collision. Expose the release as a global it can
// await. Native only — web has no shared native handle to release.
declare global {
  interface Window {
    __zsReleasePowerSync?: () => Promise<void>
  }
}
if (isNative()) {
  window.__zsReleasePowerSync = () => releaseForHardLoad()
}

// E2E test bridge — always installed in Vite dev mode so manual `npm run dev`
// (without VITE_E2E_HOOKS=1) doesn't leave Playwright tests unable to find
// window.__imki_test_hooks. Production build (DEV=false) tree-shakes the
// branch entirely.
if (import.meta.env.DEV || import.meta.env.VITE_E2E_HOOKS === '1') {
  import('@/lib/e2e/test-hooks').then(({ installTestHooks }) => installTestHooks())
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const container = document.getElementById('root')
if (!container) throw new Error('Missing #root element')

// AppRoot's providers (Auth, PowerSync, PostHog, etc.) need RouterProvider's
// context, so RouterProvider is mounted at the top and AppRoot lives inside
// __root.tsx wrapping <Outlet />.
createRoot(container).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
)

// Android hardware back button: only override if router has history,
// otherwise let the system exit the app (standard Android UX).
if (isNative() && isAndroid()) {
  CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back()
    } else {
      void CapacitorApp.exitApp()
    }
  })
}

// App resume is already covered by document `visibilitychange` (Capacitor
// WebView fires it on foreground/background transitions), which
// intent-worker / SyncStatusPill / connectivity all subscribe to. No
// additional dispatch needed here.

if (isNative()) {
  void SplashScreen.hide({ fadeOutDuration: 300 })
  void StatusBar.setStyle({ style: Style.Dark })
  if (isIos()) {
    void StatusBar.setOverlaysWebView({ overlay: false })
  }
}
